import { NodeModel } from "@minoru/react-dnd-treeview";
import { Folder, InsertChart, Search } from "@mui/icons-material";
import {
	Box,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	MenuItem,
	MenuList,
	Popover,
	TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { TagSearchResult, useTagSearch } from "../hooks/useTagSearch";
import { TagFromTree } from "../models/TrendingModelsV2";

interface TagSearchInputProps {
	tagNodes: NodeModel<TagFromTree>[];
	onSearchResultClick: (result: TagSearchResult) => void;
}

export const TagSearchInput: React.FC<TagSearchInputProps> = ({
	tagNodes,
	onSearchResultClick,
}) => {
	const searchInputRef = useRef<HTMLInputElement>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const {
		searchTerm,
		searchResults,
		showSearchResults,
		focusedSearchIndex,
		searchResultsRefs,
		handleSearchChange,
		handleKeyDown,
		clearSearch,
	} = useTagSearch({ tagNodes });

	const handleSearchResultClick = (result: TagSearchResult) => {
		onSearchResultClick(result);
		clearSearch();
		setAnchorEl(null);
	};

	const handleKeyDownWithResultSelection = (
		event: React.KeyboardEvent<HTMLDivElement>,
	) => {
		const selectedResult = handleKeyDown(event);
		if (selectedResult) {
			handleSearchResultClick(selectedResult);
		}
	};

	const handleSearchInputChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		handleSearchChange(event);
		if (!anchorEl && searchInputRef.current) {
			setAnchorEl(searchInputRef.current);
		}
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
	};

	return (
		<div
			onKeyDown={handleKeyDownWithResultSelection}
			style={{ marginBottom: 16 }}
		>
			<TextField
				placeholder="Search tags (min. 2 characters)..."
				variant="outlined"
				size="small"
				fullWidth
				value={searchTerm}
				onChange={handleSearchInputChange}
				inputRef={searchInputRef}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search color="action" />
						</InputAdornment>
					),
				}}
			/>
			<Popover
				open={showSearchResults && Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handlePopoverClose}
				disableAutoFocus
				disableEnforceFocus
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				PaperProps={{
					style: { width: anchorEl?.offsetWidth, maxHeight: 300 },
				}}
			>
				<MenuList dense>
					{searchResults.map((result, index) => (
						<MenuItem
							key={result.id}
							ref={(el) => {
								if (searchResultsRefs.current) {
									searchResultsRefs.current[index] = el;
								}
							}}
							selected={index === focusedSearchIndex}
							onClick={() => handleSearchResultClick(result)}
						>
							<ListItemIcon>
								{result.hasChildren || result.isDroppable ? (
									<Folder color="primary" fontSize="small" />
								) : (
									<InsertChart color="primary" fontSize="small" />
								)}
							</ListItemIcon>
							<ListItemText
								primary={
									<div style={{ display: "flex", alignItems: "center" }}>
										{result.tagTypeCode && result.tagTypeCode !== "F" && (
											<Box
												component="span"
												sx={{
													width: "20px",
													height: "20px",
													color: "white",
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
													fontSize: "10px",
													fontWeight: "bold",
													margin: "0 5px",
													borderRadius: "3px",
													border: "1px solid rgba(0, 0, 0, 0.2)",
													backgroundColor:
														result.tagTypeCode === "A" ? "#AD48C4" : "#C46F48",
												}}
											>
												{result.tagTypeCode}
											</Box>
										)}
										<span>{result.alias || result.name}</span>
										{result.alias && result.alias !== result.name && (
											<span
												style={{ marginLeft: 6, color: "rgba(0, 0, 0, 0.6)" }}
											>
												[{result.name}]
											</span>
										)}
									</div>
								}
								secondary={result.path}
								secondaryTypographyProps={{
									variant: "caption",
									color: "textSecondary",
									style: { fontStyle: "italic" },
								}}
							/>
						</MenuItem>
					))}
				</MenuList>
			</Popover>
		</div>
	);
};
